import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Card, Typography, Popconfirm, message, Spin, Alert, Tag, Modal, Input, Select, Row, Col, Form } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, KeyOutlined, CopyOutlined, PoweroffOutlined, SearchOutlined, ClearOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { teacherAPI, schoolAPI, schoolAdminAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;

const TeacherList = () => {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { isSuperAdmin, currentUser } = useAuth();
  const [tokenModalVisible, setTokenModalVisible] = useState(false);
  const [currentTeacherId, setCurrentTeacherId] = useState(null);
  const [customToken, setCustomToken] = useState('');
  const [tokenError, setTokenError] = useState('');
  const [tokenLoading, setTokenLoading] = useState(false);
  const [schools, setSchools] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [filters, setFilters] = useState({
    name: '',
    subject: '',
    schoolId: null,
    status: null,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  // ✅ 新增：重置密码相关状态
  const [resetPasswordVisible, setResetPasswordVisible] = useState(false);
  const [resetPasswordForm] = Form.useForm();
  const [currentTeacher, setCurrentTeacher] = useState(null);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);

  useEffect(() => {
    fetchTeachers();
    fetchSchools();
  }, []);

  const fetchSchools = async () => {
    try {
      const response = await schoolAPI.getAll();
      if (response.data.success) {
        setSchools(response.data.data);
      }
    } catch (error) {
      console.error('获取学校列表失败:', error);
    }
  };

  const fetchTeachers = async (
    page = pagination.current,
    pageSize = pagination.pageSize,
    currentFilters = filters
  ) => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page,
        limit: pageSize,
        search: currentFilters.name?.trim() || undefined,
        subject: currentFilters.subject?.trim() || undefined,
        schoolId: currentFilters.schoolId || undefined,
        status: currentFilters.status || undefined,
      };

      const response = await teacherAPI.getAll(params);
      if (response.data.success) {
        const pageInfo = response.data.pagination || {};
        setTeachers(Array.isArray(response.data.data) ? response.data.data : []);
        setPagination((previous) => ({
          ...previous,
          current: Number(pageInfo.current) || page,
          pageSize: Number(pageInfo.pageSize) || pageSize,
          total: Number(pageInfo.total) || 0,
        }));
      } else {
        setError(response.data.message || '获取教师列表失败');
      }
    } catch (err) {
      console.error('获取教师列表错误:', err);
      setError('获取教师列表失败，请稍后再试');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      const platformAdmin = isSuperAdmin();
      const schoolId = record.schoolId || record.school_id || filters.schoolId || currentUser?.schoolId || currentUser?.school_id;
      if (!platformAdmin && !schoolId) {
        message.error('无法确定当前学校，请刷新页面后重试');
        return;
      }
      const response = platformAdmin
        ? await teacherAPI.delete(record.id)
        : await schoolAdminAPI.removeTeacher(record.id, { schoolId });
      if (response.data.success) {
        message.success(platformAdmin ? '教师全局账号已删除' : '教师已移出本校，其他学校数据不受影响');
        fetchTeachers();
      } else {
        message.error(response.data.message || (platformAdmin ? '删除教师失败' : '移出本校失败'));
      }
    } catch (err) {
      console.error('教师移除错误:', err);
      message.error(err.response?.data?.message || '操作失败，请稍后再试');
    }
  };

  const handleResetToken = async (id) => {
    try {
      const response = await teacherAPI.resetToken(id);
      if (response.data.success) {
        message.success('教师口令重置成功');
        fetchTeachers();
      } else {
        message.error(response.data.message || '重置口令失败');
      }
    } catch (err) {
      console.error('重置口令失败:', err);
      message.error('重置口令失败');
    }
  };

  const handleStatusToggle = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'disabled' : 'active';
      const response = await teacherAPI.toggleStatus(id, newStatus);
      
      if (response.data.success) {
        message.success(`教师${newStatus === 'active' ? '启用' : '停用'}成功`);
        fetchTeachers();
      } else {
        message.error(response.data.message || `教师${newStatus === 'active' ? '启用' : '停用'}失败`);
      }
    } catch (err) {
      console.error('更新教师状态失败:', err);
      message.error('操作失败，请稍后再试');
    }
  };
  
  const showTokenModal = (id) => {
    setCurrentTeacherId(id);
    setCustomToken('');
    setTokenError('');
    setTokenModalVisible(true);
  };

  const handleSetCustomToken = async () => {
    if (customToken.length < 3) {
      setTokenError('口令长度必须大于等于3位');
      return;
    }
    
    try {
      setTokenLoading(true);
      const response = await teacherAPI.setToken(currentTeacherId, customToken);
      if (response.data.success) {
        message.success('教师口令设置成功');
        setTokenModalVisible(false);
        fetchTeachers();
      } else {
        setTokenError(response.data.message || '设置口令失败');
      }
    } catch (err) {
      console.error('设置口令失败:', err);
      setTokenError('设置口令失败，请稍后再试');
    } finally {
      setTokenLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => {
        message.success('口令已复制到剪贴板');
      },
      () => {
        message.error('复制失败，请手动复制');
      }
    );
  };

  // ✅ 新增：显示重置密码对话框
  const showResetPasswordModal = (teacher) => {
    setCurrentTeacher(teacher);
    setResetPasswordVisible(true);
    resetPasswordForm.resetFields();
  };

  // ✅ 新增：重置密码
  const handleResetPassword = async (values) => {
    try {
      setResetPasswordLoading(true);
      
      // ✅ 使用axios直接调用，但需要手动添加token
      const response = await teacherAPI.resetPassword(currentTeacher.id, values.newPassword);
      
      if (response.data.success) {
        message.success('密码重置成功');
        Modal.success({
          title: '密码重置成功',
          content: (
            <div>
              <p>教师：{currentTeacher.name}</p>
              <p>用户名：<strong>{response.data.data.username}</strong></p>
              <p>新密码：<strong>{values.newPassword}</strong></p>
              <p style={{color: '#999', marginTop: 10}}>请告知教师新密码</p>
            </div>
          )
        });
        setResetPasswordVisible(false);
        fetchTeachers();
      } else {
        message.error(response.data.message || '重置失败');
      }
    } catch (err) {
      console.error('重置密码失败:', err);
      const errorMsg = err.response?.data?.message || '重置密码失败';
      message.error(errorMsg);
    } finally {
      setResetPasswordLoading(false);
    }
  };

  // 处理筛选条件变化
  const handleFilterChange = (name, value) => {
    setFilters({
      ...filters,
      [name]: value
    });
  };

  // 处理快速搜索
  const handleQuickSearch = (value) => {
    const newFilters = { ...filters, name: value };
    setFilters(newFilters);
    applyFilters(newFilters);
  };

  // 应用筛选
  const applyFilters = async (currentFilters = filters) => {
    setSearchLoading(true);
    try {
      await fetchTeachers(1, pagination.pageSize, currentFilters);
    } finally {
      setSearchLoading(false);
    }
  };

  // 重置筛选
  const resetFilters = () => {
    const emptyFilters = {
      name: '',
      subject: '',
      schoolId: null,
      status: null,
    };
    setFilters(emptyFilters);
    applyFilters(emptyFilters);
  };

  const handleTableChange = (nextPagination) => {
    fetchTeachers(nextPagination.current, nextPagination.pageSize, filters);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '学科',
      dataIndex: 'subject',
      key: 'subject',
      render: (text) => text || '-'
    },
    {
      title: '所属学校',
      dataIndex: 'school_name',
      key: 'school_name',
    },
    {
      title: '登录凭据',
      key: 'login_info',
      render: (_, record) => {
        const loginUsername = record.login_username || record.username;
        const loginEmail = record.login_email || record.email;
        const tokenValue = record.token || record.tokenHint || record.token_hint;

        return (
          <div>
            {loginUsername ? (
            <div>
              <div style={{ marginBottom: '4px' }}>
                <Text strong>用户名: </Text>
                <Text copyable>{loginUsername}</Text>
              </div>
              {loginEmail && (
                <div style={{ marginBottom: '4px' }}>
                  <Text strong>邮箱: </Text>
                  <Text copyable>{loginEmail}</Text>
                </div>
              )}
              <div style={{ fontSize: '12px', color: '#666' }}>
                使用用户名/邮箱 + 注册密码登录
              </div>
            </div>
          ) : (
            <div>
              <div style={{ marginBottom: '4px' }}>
                <Text strong>Token: </Text>
                {tokenValue ? (
                  <Text copyable>{tokenValue}</Text>
                ) : (
                  <Text type="secondary">未设置</Text>
                )}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {tokenValue ? '仅显示脱敏Token（旧版教师）' : '暂无可用登录凭据'}
              </div>
            </div>
          )}
        </div>
        );
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? '正常' : '已停用'}
        </Tag>
      )
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => text ? new Date(text).toLocaleString() : '-'
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          {isSuperAdmin() && (
            <>
              <Button
                type="primary"
                icon={<EditOutlined />}
                size="small"
                onClick={() => navigate(`/teachers/${record.id}/edit`)}
              >
                编辑全局账号
              </Button>

              <Button
                type={record.status === 'active' ? 'danger' : 'primary'}
                icon={<PoweroffOutlined />}
                size="small"
                onClick={() => handleStatusToggle(record.id, record.status)}
              >
                {record.status === 'active' ? '停用全局账号' : '启用全局账号'}
              </Button>

              <Button
                type="primary"
                icon={<KeyOutlined />}
                size="small"
                onClick={() => showTokenModal(record.id)}
              >
                设置登录口令
              </Button>

              <Button
                type="default"
                size="small"
                onClick={() => showResetPasswordModal(record)}
              >
                重置全局密码
              </Button>
            </>
          )}
          
          <Button 
            type="primary" 
            onClick={() => navigate(`/teachers/bind-classes/${record.id}?schoolId=${record.school_id}`)}
            size="small"
          >
            班级绑定
          </Button>
          
          {currentUser && currentUser.role !== 'teacher' && (
            <Popconfirm
              title={isSuperAdmin()
                ? '确定删除该教师的全局账号吗？此操作会影响其所有学校。'
                : '确定将该教师移出当前学校吗？其他学校及全局账号不受影响。'}
              onConfirm={() => handleDelete(record)}
              okText="确定"
              cancelText="取消"
            >
              <Button type="primary" danger icon={<DeleteOutlined />} size="small">
                {isSuperAdmin() ? '删除全局账号' : '移出本校'}
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Title level={2}>教师管理</Title>
            {currentUser && currentUser.role !== 'teacher' && (
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={() => navigate('/teachers/create')}
              >
                添加教师
              </Button>
            )}
          </div>
          
          {/* 筛选区域 */}
          <div style={{ background: '#f6f6f6', padding: '15px', borderRadius: '5px' }}>
            <Row gutter={16}>
              <Col sm={24} md={8} lg={6}>
                <Form.Item label="教师姓名" style={{ marginBottom: 0 }}>
                  <Search
                    placeholder="搜索教师姓名"
                    value={filters.name}
                    onChange={(e) => handleFilterChange('name', e.target.value)}
                    onSearch={handleQuickSearch}
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col sm={24} md={8} lg={6}>
                <Form.Item label="学科" style={{ marginBottom: 0 }}>
                  <Input
                    placeholder="搜索学科"
                    value={filters.subject}
                    onChange={(e) => handleFilterChange('subject', e.target.value)}
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col sm={24} md={8} lg={6}>
                <Form.Item label="所属学校" style={{ marginBottom: 0 }}>
                  <Select
                    placeholder="选择学校"
                    value={filters.schoolId}
                    onChange={(value) => handleFilterChange('schoolId', value)}
                    allowClear
                    style={{ width: '100%' }}
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {schools.map(school => (
                      <Option key={school.id} value={school.id}>{school.name}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col sm={24} md={8} lg={6}>
                <Form.Item label="教师状态" style={{ marginBottom: 0 }}>
                  <Select
                    placeholder="选择状态"
                    value={filters.status}
                    onChange={(value) => handleFilterChange('status', value)}
                    allowClear
                    style={{ width: '100%' }}
                  >
                    <Option value="active">正常</Option>
                    <Option value="disabled">已停用</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row style={{ marginTop: '10px' }}>
              <Col span={24} style={{ textAlign: 'right' }}>
                <Space>
                  <Button 
                    icon={<ClearOutlined />} 
                    onClick={resetFilters}
                  >
                    重置
                  </Button>
                  <Button 
                    type="primary" 
                    icon={<SearchOutlined />} 
                    onClick={() => applyFilters()}
                    loading={searchLoading}
                  >
                    筛选
                  </Button>
                </Space>
              </Col>
            </Row>
          </div>
          
          {error && <Alert message={error} type="error" showIcon />}
          
          <Spin spinning={loading}>
            <Table 
              columns={columns} 
              dataSource={teachers}
              rowKey="id"
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showTotal: (total) => `共 ${total} 名教师`,
              }}
              onChange={handleTableChange}
            />
          </Spin>
        </Space>
      </Card>
      
      <Modal
        title="设置教师口令"
        open={tokenModalVisible}
        onCancel={() => setTokenModalVisible(false)}
        footer={[
          <Button key="reset" onClick={() => handleResetToken(currentTeacherId)}>
            自动生成口令
          </Button>,
          <Button key="cancel" onClick={() => setTokenModalVisible(false)}>
            取消
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            loading={tokenLoading}
            onClick={handleSetCustomToken}
          >
            确定
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <p>请输入新的教师登录口令（至少3位字符）：</p>
          <Input
            value={customToken}
            onChange={(e) => setCustomToken(e.target.value)}
            placeholder="请输入新的教师登录口令"
          />
          {tokenError && <div style={{ color: 'red', marginTop: 8 }}>{tokenError}</div>}
        </div>
      </Modal>
      
      {/* ✅ 新增：重置密码对话框 */}
      <Modal
        title="重置教师密码"
        open={resetPasswordVisible}
        onCancel={() => setResetPasswordVisible(false)}
        footer={null}
      >
        <Form
          form={resetPasswordForm}
          onFinish={handleResetPassword}
          layout="vertical"
        >
          <Alert
            message="提示"
            description={`即将为教师【${currentTeacher?.name}】重置密码。该教师必须已有账号密码登录方式。`}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码至少6个字符' }
            ]}
          >
            <Input.Password placeholder="请输入新密码（至少6位）" />
          </Form.Item>
          
          <Form.Item
            name="confirmPassword"
            label="确认密码"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="请再次输入新密码" />
          </Form.Item>
          
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setResetPasswordVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit" loading={resetPasswordLoading}>
                确定重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default TeacherList; 
